import React, { useMemo } from "react";
import "./Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const Terms = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients across cities",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Terms of use</p>
          <h1>Terms &amp; Conditions</h1>

          <p className="pp-meta">
            <span>Service provider: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
        </header>

        {/* Content */}
        <div className="privacy-policy-content">
          <section>
            <h2>1. What these Terms cover</h2>
            <p>
              These Terms explain how you can use our website and how we work with you
              when you hire us for services like website design, landing pages, Google
              &amp; Meta ads, SEO or other digital marketing support.
            </p>
            <p>
              For most projects, we will also share a simple proposal or agreement that
              lists the scope, price and timelines. If anything in that written
              agreement is different from what is written here, the signed proposal or
              agreement will be followed for that project.
            </p>
          </section>

          <section>
            <h2>2. Using our website</h2>
            <p>
              You promise to use our website in a fair and legal way. Please do not try
              to hack, damage or overload the site, or do anything that stops other
              people from using it normally.
            </p>
            <p>
              All the text, images, layouts and code you see here belong to{" "}
              {brand.name} or our partners. You are welcome to read and share links to
              our content, but you should not copy it as your own, republish it, or use
              it as a template for your own services without our written permission.
            </p>
          </section>

          <section>
            <h2>3. Quotes, scope &amp; changes</h2>
            <p>
              Before we start any paid work, we will agree in writing (usually email or
              a proposal) on:
            </p>
            <ul>
              <li>what we will do for you (the scope),</li>
              <li>how long it should roughly take, and</li>
              <li>how much it will cost and how payment works.</li>
            </ul>
            <p>
              If you later ask for extra work that is not part of this agreed scope
              (for example extra pages, campaigns, designs or integrations), we may:
            </p>
            <ul>
              <li>send a revised quote for the extra work; or</li>
              <li>treat it as a new mini-project or new phase.</li>
            </ul>
            <p>
              We will always try to explain clearly what is included and what is an
              add-on before we start.
            </p>
          </section>

          <section>
            <h2>4. What we need from you</h2>
            <p>
              To deliver good work on time, we need some help from your side. You
              agree to:
            </p>
            <ul>
              <li>
                Give us access we reasonably need (for example to your website, ad
                accounts, analytics, or hosting).
              </li>
              <li>
                Share content like text, images, logos and brand guidelines in a timely
                manner.
              </li>
              <li>
                Review drafts and give clear feedback within a reasonable time so the
                project keeps moving.
              </li>
              <li>
                Make sure anything you give us (content, images, data) is accurate, legal
                and does not copy someone else’s protected work without permission.
              </li>
            </ul>
            <p>
              If feedback, content or approvals are delayed, timelines for design,
              development, ad performance and launch dates may need to change, and
              additional costs may apply if the delay is significant.
            </p>
          </section>

          <section>
            <h2>5. Fees, payments &amp; refunds</h2>
            <p>
              Our fees and payment terms will be clearly mentioned in your proposal or
              invoice. Unless we agree otherwise in writing:
            </p>
            <ul>
              <li>
                Invoices are due within the payment period shown on the invoice (for
                example, 7 or 15 days).
              </li>
              <li>
                We may pause work or delay launches if invoices are unpaid for a long
                time, after letting you know.
              </li>
              <li>
                Deposits, retainers and fees for work already completed are normally
                non-refundable.
              </li>
            </ul>
            <p>
              Costs like ad spend (Google, Meta), premium plugins, stock photos,
              hosting, domains or third-party tools are usually separate from our
              service fees. These are either:
            </p>
            <ul>
              <li>paid by you directly to the provider; or</li>
              <li>
                paid by us and then re-billed to you, as agreed in advance.
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Ownership of work</h2>
            <p>
              {brand.name} keeps ownership of our internal tools, processes, code
              libraries and methods that we use across many projects. These are part of
              how we work and are not sold as part of any one project.
            </p>
            <p>
              Once you have paid all agreed fees for a project, you have the right to
              use the final agreed deliverables for your own business — for example your
              website design, landing pages, ad creatives and basic copy we created for
              you under that project.
            </p>
            <p>
              You should not resell our work as a ready-made theme, template or product
              to multiple other clients without our written permission.
            </p>
          </section>

          <section>
            <h2>7. Third-party tools &amp; services</h2>
            <p>
              Many digital marketing projects use third-party tools like hosting,
              analytics, email marketing platforms, payment gateways, ad networks or SEO
              tools.
            </p>
            <p>
              These tools have their own terms, prices and privacy policies. You are
              responsible for reading and agreeing to those where required.{" "}
              {brand.name} cannot control their uptime, pricing changes, policy updates
              or technical problems, but we will try to support you with reasonable
              troubleshooting when issues appear.
            </p>
          </section>

          <section>
            <h2>8. Confidential information</h2>
            <p>
              Both we and you agree to keep private any non-public information that we
              share with each other during the project — for example logins, business
              plans, internal reports, customer data and financial information.
            </p>
            <p>
              We may share some information with trusted team members or partners only
              when needed to deliver your work, and we expect them to respect the same
              confidentiality. These obligations continue even after our project ends.
            </p>
          </section>

          <section>
            <h2>9. Use of AI &amp; automation</h2>
            <p>
              Sometimes we may use trusted AI and automation tools to help with tasks
              like draft copy, ad ideas, SEO checks or testing. This is to save time and
              explore more options for your benefit.
            </p>
            <p>
              We review and adjust this work before sharing it with you, but AI tools
              can sometimes make mistakes or produce suggestions that are not appropriate
              for your situation. You should always review final content and discuss any
              concerns with us before publishing.
            </p>
          </section>

          <section>
            <h2>10. No guaranteed results</h2>
            <p>
              We aim to do our best for every project and use our experience to improve
              your online presence. However, digital marketing results (like Google
              rankings, number of leads, sales or revenue) depend on many factors we
              cannot fully control — including your industry, competition, budgets,
              platform changes and your own internal processes.
            </p>
            <p>
              For this reason, {brand.name} does not promise or guarantee a specific
              number of leads, sales or a certain position on Google.
            </p>
          </section>

          <section>
            <h2>11. Limitation of liability</h2>
            <p>
              As far as the law allows, {brand.name} and our team will not be
              responsible for any indirect or special damages, such as lost profits,
              lost data, or lost opportunities, that result from using our website or
              our services.
            </p>
            <p>
              If we are found to be legally responsible for something, our total
              liability related to a specific project will be limited to the total
              amount you paid us for that project in the three (3) months just before
              the issue happened.
            </p>
          </section>

          <section>
            <h2>12. Timelines, pauses &amp; ending a project</h2>
            <p>
              Estimated timelines and key dates will be mentioned in your proposal or
              plan. These are based on smooth collaboration and may change if scope
              changes or if there are delays in feedback, payments or third-party
              approvals.
            </p>
            <p>
              In many cases, either of us can end the engagement with written notice, as
              described in the proposal or agreement. If this happens, you agree to pay
              for:
            </p>
            <ul>
              <li>work completed up to the date the project ends; and</li>
              <li>
                any third-party costs we have already committed to on your behalf that
                cannot be refunded.
              </li>
            </ul>
          </section>

          <section>
            <h2>13. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time if our services, processes or
              legal requirements change. When we do, we will update the “Last updated”
              date at the top of this page.
            </p>
            <p>
              If you continue to use our website or services after new Terms are posted,
              it means you accept the updated version.
            </p>
          </section>

          <section>
            <h2>14. Law &amp; disputes</h2>
            <p>
              These Terms are governed by the laws of the place where {brand.name} is
              registered, unless any separate written agreement between us says
              otherwise.
            </p>
            <p>
              If there is a dispute relating to these Terms, our website or our services,
              we will first try to resolve it by talking it through in good faith. If
              that does not work, the matter will normally be handled by the courts in
              that same location, unless we both agree in writing on a different
              approach.
            </p>
          </section>

          <section>
            <h2>15. Privacy &amp; personal data</h2>
            <p>
              To understand how we collect and use personal information (for example
              contact details you enter on our forms), please read our{" "}
              <a href="/privacy-policy">Privacy Policy</a>.
            </p>
            <p>
              Together, these Terms and the Privacy Policy explain how we operate, how
              we handle your data and what rights you may have.
            </p>
          </section>

          <section>
            <h2>16. How to contact us</h2>
            <p>
              If you have any questions about these Terms or how they apply to your
              project, you can reach us here:
            </p>
            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {brand.email}
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                  {brand.phone}
                </a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Terms;
